import { Tokens, apiBaseURL, authActionType, toastifyType } from '../../../constants';
import { environment } from "../../../config/environment"
import { setLoading } from '../loadingAction';
import { addTost } from '../toastAction';
import apiConfig from '../../../config/apiConfig';
import { setSecureItem, getSecureItem, removeSecureItem } from '../../../lib/secureStorage';

/** Persists a completed sign-in (single- or second-factor) and redirects. */
const completeSignIn = (dispatch, data, message) => {
  setSecureItem(Tokens.ADMIN_TOKEN, data?.token);
  setSecureItem(Tokens.ADMIN, JSON.stringify(data?.user));
  setSecureItem(Tokens.ADMIN_ROLE, data?.user?.role);
  dispatch({ type: authActionType.LOGIN_ADMIN, payload: data });
  dispatch(addTost({ type: toastifyType.SHOW_SUCCESS_MESSAGE, message }));
  if (data?.mfaEnrolmentRequired) {
    // ORG-007: the organisation requires MFA and this account has none —
    // land on the profile security card instead of the dashboard.
    window.location.href = "/account/profile?mfa=required";
    return;
  }
  if (data?.user?.role === 'user') {
    window.location.href = "/";
  } else {
    window.location.href = "/admin";
  }
};

/**
 * onMfaChallenge: called instead of redirecting when the account has a
 * second factor — the login page swaps to the code screen (ORG-007).
 */
export const login = (user, { onMfaChallenge } = {}) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await apiConfig.post(apiBaseURL.ADMIN_LOGIN, user);
    if (response?.data?.success) {
      dispatch(setLoading(false));
      if (response?.data?.data?.mfaRequired) {
        onMfaChallenge && onMfaChallenge(response.data.data.challengeId);
        return;
      }
      completeSignIn(dispatch, response?.data?.data, response.data.message);
    } else {
      dispatch(setLoading(false));
    }
  } catch (error) {
    dispatch(setLoading(false));
    dispatch(addTost({ type: toastifyType.SHOW_ERROR_MESSAGE, message: error?.response?.data?.message }));
  }
};

/** ORG-007: completes the second factor and lands the session. */
export const verifyMfaLogin = ({ challengeId, code }) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await apiConfig.post('auth/mfa/verify', { challengeId, code });
    dispatch(setLoading(false));
    if (response?.data?.success) {
      completeSignIn(dispatch, response?.data?.data, response.data.message);
      return true;
    }
    return false;
  } catch (error) {
    dispatch(setLoading(false));
    dispatch(addTost({ type: toastifyType.SHOW_ERROR_MESSAGE, message: error?.response?.data?.message }));
    return false;
  }
};

/**
 * ORG-008: SSO initiation — the backend resolves the governed provider for
 * the email domain and returns the IdP redirect URL.
 */
export const ssoInitiate = (email) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await apiConfig.post('auth/sso', { email });
    dispatch(setLoading(false));
    const url = response?.data?.data?.url;
    if (url) {
      window.location.href = url;
      return true;
    }
    dispatch(addTost({ type: toastifyType.SHOW_ERROR_MESSAGE, message: 'The identity provider did not return a redirect.' }));
    return false;
  } catch (error) {
    dispatch(setLoading(false));
    dispatch(addTost({ type: toastifyType.SHOW_ERROR_MESSAGE, message: error?.response?.data?.message }));
    return false;
  }
};

/**
 * ORG-008: after the IdP callback the API origin holds a session cookie —
 * exchange it once (withCredentials) for the bearer token + user payload.
 * Same MFA challenge step as password sign-in.
 */
export const completeSsoSession = ({ onMfaChallenge } = {}) => async (dispatch) => {
  try {
    const response = await apiConfig.post('auth/sso/session', {}, { withCredentials: true });
    if (response?.data?.success) {
      if (response?.data?.data?.mfaRequired) {
        onMfaChallenge && onMfaChallenge(response.data.data.challengeId);
        return true;
      }
      completeSignIn(dispatch, response?.data?.data, response.data.message);
      return true;
    }
    return false;
  } catch (error) {
    dispatch(addTost({ type: toastifyType.SHOW_ERROR_MESSAGE, message: error?.response?.data?.message || 'Single sign-on could not complete.' }));
    return false;
  }
};

export const logout = ({ navigate, setLogoutLoading }) => async (dispatch) => {
  try {
    setLogoutLoading && setLogoutLoading(true);
    const response = await apiConfig.get(apiBaseURL.LOGOUT);
    removeSecureItem(Tokens.ADMIN_TOKEN);
    removeSecureItem(Tokens.ADMIN);
    removeSecureItem(Tokens.ADMIN_ROLE);
    dispatch({ type: authActionType.LOGOUT, payload: response?.data?.data });
  } catch {
    // clear tokens even if the API call fails
    removeSecureItem(Tokens.ADMIN_TOKEN);
    removeSecureItem(Tokens.ADMIN);
    removeSecureItem(Tokens.ADMIN_ROLE);
  } finally {
    window.location.href = environment.BASE_URL + '/signin';
    setTimeout(() => {
      setLogoutLoading && setLogoutLoading(false);
      dispatch(setLoading(false));
    }, 100);
  }
};
